import { ChatBlockDisclosure, type ChatBlockContext } from "../block-disclosure";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { TermRow } from "../../term/term-row";
import {
  isRunningStatus,
  setRunningPulse,
} from "../chat-block-plugin";
import type { ChatBlockPlugin } from "../chat-block-plugin";
import type { ChatContentBlock } from "../chat-types";

/**
 * The claude-cli dialect's block grammar.
 *
 * These three plugins are not general block kinds — they are the Claude Code
 * transcript's own way of drawing prose, the prompt band and a tool call, and
 * `ChatClaudeTurn.pluginTypeFor` is the only thing that routes onto them. They
 * live beside the look that declares them so that `components/chat` holds only
 * the vocabulary every look shares.
 */

/**
 * Assistant prose in Claude Code's grammar: every message opens with the
 * `⏺` marker in the transcript's own ink (the STATUS-coloured `⏺` belongs
 * to tool calls, not prose) and continuation lines hang at the marker's
 * indent. The Claude dialect of the terminal register routes assistant
 * text here; the reference for the grammar and its default colours is
 * Claude Code itself.
 */
export class ClaudeTextBlockPlugin
  implements ChatBlockPlugin<HTMLParagraphElement>
{
  type = "text_claude";

  makeBlock(): HTMLParagraphElement {
    const $p = document.createElement("p");
    $p.style.margin = "0";
    $p.style.whiteSpace = "pre-wrap";
    $p.style.wordBreak = "break-word";
    $p.style.paddingLeft = "2ch";
    $p.style.textIndent = "-2ch";
    return $p;
  }

  bindBlock(
    $block: HTMLParagraphElement,
    content: ChatContentBlock,
    _isStreaming: boolean,
  ): void {
    if (content.type !== "text") return;
    $block.textContent = `⏺ ${content.text}`;
  }

  appendDelta($block: HTMLParagraphElement, delta: string): void {
    $block.textContent += delta;
  }

  finalizeBlock(): void {}

  onRecycle($block: HTMLParagraphElement): void {
    $block.textContent = "";
  }
}

/**
 * The user's prompt row in Claude Code's grammar: a `❯` caret one cell
 * before the text. The full-width band behind the row is the LOOK's — the
 * theme sheet grounds `std-chat-claude-turn[user]` exactly as the codex
 * sheet bands its own user turns — so this plugin owns only the row's own
 * grammar. The caret is a hook because Claude Code draws it quieter than
 * the text (#4e4e4e against white, from the captured component).
 */
export class ClaudePromptBlockPlugin
  implements ChatBlockPlugin<HTMLParagraphElement>
{
  type = "prompt_claude";

  makeBlock(): HTMLParagraphElement {
    const $p = document.createElement("p");
    $p.style.margin = "0";
    $p.style.whiteSpace = "pre-wrap";
    $p.style.wordBreak = "break-word";
    $p.style.paddingLeft = "2ch";
    $p.style.textIndent = "-2ch";
    const $caret = document.createElement("span");
    $caret.style.color = "var(--chat-claude-caret, #4e4e4e)";
    $caret.textContent = "❯ ";
    const $text = document.createElement("span");
    $p.append($caret, $text);
    return $p;
  }

  bindBlock(
    $block: HTMLParagraphElement,
    content: ChatContentBlock,
    _isStreaming: boolean,
  ): void {
    if (content.type !== "text") return;
    ($block.lastElementChild as HTMLElement).textContent = content.text;
  }

  appendDelta($block: HTMLParagraphElement, delta: string): void {
    ($block.lastElementChild as HTMLElement).textContent += delta;
  }

  finalizeBlock(): void {}

  onRecycle($block: HTMLParagraphElement): void {
    ($block.lastElementChild as HTMLElement).textContent = "";
  }
}

/**
 * A tool call in Claude Code's grammar, from the CLI's own transcript:
 *
 *     ⏺ Bash(cargo test -q)
 *       ⎿  running 42 tests … +18 lines (ctrl+o to expand)
 *
 * The `⏺` carries STATUS as colour (green ok, red failed, amber pending —
 * the captured component's exact hexes as hook defaults); the label is the
 * same `Bash(inner)` idiom as the card form, split so the name, the parens,
 * and the argument each take their own ink; the `⎿` connector line carries
 * the collapsed summary and Claude Code's literal expand hint. Clicking
 * expands the raw invocation and output rows under the connector —
 * expansion state keyed on the content-block OBJECT, as in the other
 * tool-call forms, so it survives the recycler.
 */
export class ClaudeToolCallBlockPlugin implements ChatBlockPlugin<HTMLElement> {
  type = "tool_call_claude";
  private expanded = new ChatBlockDisclosure();
  private state = new WeakMap<
    HTMLElement,
    {
      block: {
        tool: Option<string>;
        command: string;
        output: Option<string>;
        status: Option<string>;
      };
      $marker: HTMLElement;
      $name: HTMLElement;
      $arg: HTMLElement;
      $summary: HTMLElement;
      $hint: HTMLElement;
      $rows: HTMLElement;
    }
  >();

  makeBlock(): HTMLElement {
    const $root = document.createElement("div");
    $root.style.whiteSpace = "var(--chat-terminal-flow, pre-wrap)";
    $root.style.wordBreak = "var(--chat-terminal-break, break-all)";

    const $head = document.createElement("div");
    $head.style.paddingLeft = "2ch";
    $head.style.textIndent = "-2ch";
    const $marker = document.createElement("span");
    const $name = document.createElement("span");
    const $open = document.createElement("span");
    $open.style.color = "var(--chat-claude-dim, #565f89)";
    $open.textContent = "(";
    const $arg = document.createElement("span");
    $arg.style.color = "var(--chat-claude-arg, #7dcfff)";
    const $close = document.createElement("span");
    $close.style.color = "var(--chat-claude-dim, #565f89)";
    $close.textContent = ")";
    $head.append($marker, $name, $open, $arg, $close);

    const $sub = document.createElement("div");
    $sub.style.paddingLeft = "2ch";
    const $elbow = document.createElement("span");
    $elbow.style.color = "var(--chat-claude-dim, #565f89)";
    $elbow.textContent = "⎿  ";
    const $summary = document.createElement("span");
    $summary.style.color = "var(--chat-claude-result, #8b8fa3)";
    const $hint = document.createElement("span");
    $hint.style.color = "var(--chat-claude-dim, #565f89)";
    $sub.append($elbow, $summary, $hint);

    const $rows = document.createElement("div");
    $rows.style.display = "none";
    $rows.style.paddingLeft = "4ch";
    $rows.style.color = "var(--chat-claude-result, #8b8fa3)";

    $root.append($head, $sub, $rows);

    GESTURE_MANAGER.addPointerEvent($root, {
      onClick: () => {
        const s = this.state.get($root);
        if (!s || s.block.output == null || s.block.output.length === 0) return;
        this.expanded.set(s.block, !(this.expanded.get(s.block) ?? false));
        this.render($root);
      },
      allowTextSelection: true,
    });
    return $root;
  }

  bindBlock($block: HTMLElement, content: ChatContentBlock, _streaming: boolean, context?: ChatBlockContext): void {
    if (content.type !== "tool_call") return;
    this.expanded.bind(content, context);
    const [$head, $sub, $rows] = $block.children as unknown as HTMLElement[];
    const [$marker, $name, , $arg] = $head.children as unknown as HTMLElement[];
    const [, $summary, $hint] = $sub.children as unknown as HTMLElement[];
    this.state.set($block, {
      block: content,
      $marker,
      $name,
      $arg,
      $summary,
      $hint,
      $rows,
    });
    this.render($block);
  }

  private innerCommand(command: string): string {
    let inner = command;
    const m = inner.match(/^\s*(?:\/bin\/)?(?:zsh|bash|sh)\s+-l?c\s+([\s\S]+)$/);
    if (m) {
      inner = m[1].trim();
      const q = inner[0];
      if ((q === "'" || q === '"') && inner.length >= 2 && inner.endsWith(q)) {
        inner = inner.slice(1, -1);
      }
    }
    return inner;
  }

  private render($block: HTMLElement): void {
    const s = this.state.get($block);
    if (!s) return;
    const { block } = s;
    const outputLines =
      block.output == null || block.output.length === 0
        ? []
        : block.output.split("\n");
    const isOpen = outputLines.length > 0 && (this.expanded.get(block) ?? false);
    $block.style.cursor = outputLines.length === 0 ? "default" : "pointer";

    s.$marker.textContent = "⏺ ";
    s.$marker.style.color =
      block.status === "failed"
        ? "var(--chat-claude-err, #f7768e)"
        : block.status == null || block.status === "completed"
          ? "var(--chat-claude-ok, #4ea96f)"
          : "var(--chat-claude-pending, #e0af68)";
    setRunningPulse(s.$marker, isRunningStatus(block.status));
    s.$name.textContent =
      block.tool == null || block.tool === "commandExecution"
        ? "Bash"
        : block.tool;
    s.$arg.textContent = this.innerCommand(block.command).split("\n")[0];

    if (isOpen) {
      s.$summary.textContent = `${outputLines.length} lines`;
      s.$hint.textContent = "";
      DomUtils.clearChildren(s.$rows);
      for (const raw of [...block.command.split("\n"), ...outputLines]) {
        const $row = TermRow.$row(TermRow.parse(raw));
        if (TermRow.parse(raw).text.trim().length === 0) {
          $row.style.minHeight = "1lh";
        }
        s.$rows.appendChild($row);
      }
      s.$rows.style.display = "block";
    } else {
      // Claude Code's own collapsed forms: "(No content)" when the tool
      // returned nothing, otherwise the first line, the elided count, and
      // the literal hint — a replica keeps the words.
      if (outputLines.length === 0) {
        s.$summary.textContent = "(No content)";
        s.$hint.textContent = "";
      } else {
        s.$summary.textContent =
          outputLines[0] +
          (outputLines.length > 1 ? ` … +${outputLines.length - 1} lines` : "");
        s.$hint.textContent = " (ctrl+o to expand)";
      }
      DomUtils.clearChildren(s.$rows);
      s.$rows.style.display = "none";
    }
  }

  appendDelta($block: HTMLElement): void {
    // Render-only: the thread canonicalized the delta into the model, and
    // `state.block` IS the model object — appending here would double it.
    this.render($block);
  }

  finalizeBlock(): void {}

  onRecycle($block: HTMLElement): void {
    const s = this.state.get($block);
    if (s) setRunningPulse(s.$marker, false);
    this.state.delete($block);
  }
}
